import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { FaAngleDown, FaAngleUp } from 'react-icons/fa6';

import FilterTable, { FilterFieldType } from './FilterTable';
import Pagination from './Pagination';
import {
  BodyCell,
  BodyRow,
  EmptyMessage,
  Footer,
  HeaderCell,
  HeaderContent,
  HeaderRow,
  IconCell,
  RowActionButton,
  StyledTable,
  TableScroll,
  TableSection,
} from './styles';

const DEFAULT_PAGE_SIZES = [5, 10, 20, 50, 100];

export type CustomTableHeader<T extends Record<string, unknown>> = {
  text: string;
  key?: keyof T & string;
  width?: number;
  fieldType?: FilterFieldType;
  renderable?: boolean;
  renderCell?: (value: T[keyof T], row: T) => React.ReactNode;
  filterDefaultValue?: string;
  textAlign?: 'left' | 'center' | 'right';
};

export type CustomTableDataHeader<T extends Record<string, unknown>> =
  CustomTableHeader<T> & {
    key: keyof T & string;
  };

export interface CustomTableProps<T extends Record<string, unknown>> {
  headers: Array<CustomTableHeader<T>>;
  data: T[];
  onRowClick?: (row: T, index: number) => void;
  showIconOffcanvas?: boolean;
  onClickOffCanvas?: (row: T, index: number) => void;
  itemsPerPage?: number;
  sortable?: boolean;
  showPagination?: boolean;
  minHeight?: string;
  emptyMessage?: string;
  filterable?: boolean;
  filterPlaceholder?: string;
  'data-testid'?: boolean | string;
  name?: string;
}

type SortDirection = 'asc' | 'desc';

type SortConfig<T extends Record<string, unknown>> = {
  key: keyof T & string;
  direction: SortDirection;
};

const asNode = (value: unknown): React.ReactNode => {
  if (value === null || value === undefined) return '-';
  if (React.isValidElement(value)) return value;
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return value as React.ReactNode;
};

/**
 * Essa função compara dois valores para ordenação.
 * @param valueA Primeiro valor a ser comparado
 * @param valueB Segundo valor a ser comparado
 * @param direction Direção da ordenação ('asc' para ascendente, 'desc' para descendente)
 * @returns Número indicando a ordem dos valores (-1, 0, 1)
 */
const compareValues = (
  valueA: unknown,
  valueB: unknown,
  direction: SortDirection,
) => {
  if (valueA === valueB) return 0;

  const factor = direction === 'asc' ? 1 : -1;

  if (typeof valueA === 'number' && typeof valueB === 'number') {
    return valueA > valueB ? factor : -factor;
  }

  const stringA = String(valueA ?? '');
  const stringB = String(valueB ?? '');

  return stringA.localeCompare(stringB) * factor;
};

const CustomTable = <T extends Record<string, unknown>>({
  headers,
  data,
  onRowClick,
  showIconOffcanvas = false,
  onClickOffCanvas,
  itemsPerPage = 20,
  sortable = true,
  showPagination = true,
  minHeight,
  emptyMessage = 'Nenhum registro encontrado',
  filterable = false,
  filterPlaceholder = 'Filtrar...',
  'data-testid': dataTestId,
  name,
}: CustomTableProps<T>) => {
  // Lógica de geração de testId
  const testId =
    typeof dataTestId === 'string'
      ? dataTestId
      : dataTestId
        ? `table-${name || 'data'}`
        : undefined;

  const [sortConfig, setSortConfig] = useState<SortConfig<T> | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(itemsPerPage);
  const [goToPageValue, setGoToPageValue] = useState('');

  const defaultFilters = useMemo(() => {
    if (!filterable) return {};
    const defaults: Record<string, string> = {};
    headers.forEach((header) => {
      if (header.key && header.filterDefaultValue !== undefined) {
        defaults[header.key] = header.filterDefaultValue;
      }
    });
    return defaults;
  }, [filterable, headers]);

  const [filters, setFilters] = useState<Record<string, string>>(
    filterable ? defaultFilters : {},
  );

  useEffect(() => {
    setRowsPerPage(itemsPerPage);
  }, [itemsPerPage]);

  useEffect(() => {
    if (!filterable) {
      setFilters({});
    } else {
      setFilters(defaultFilters);
    }
  }, [filterable, defaultFilters]);

  const getComparableValue = useCallback((value: unknown) => {
    if (value === null || value === undefined) return null;
    if (typeof value === 'string' || typeof value === 'number') return value;
    if (React.isValidElement<{ children?: React.ReactNode }>(value)) {
      const children = value.props?.children;
      if (typeof children === 'string' || typeof children === 'number') {
        return children;
      }
    }
    if (typeof value === 'boolean') {
      return value;
    }
    return String(value);
  }, []);

  const normalizeDateValue = useCallback((value: string | number) => {
    const stringValue = String(value);
    if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
      return stringValue;
    }

    const match = /^([0-3]\d)\/([0-1]\d)\/(\d{4})$/.exec(stringValue);
    if (match) {
      const [, day, month, year] = match;
      return `${year}-${month}-${day}`;
    }

    return stringValue;
  }, []);

  const getUniqueOptions = useCallback(
    (key: keyof T & string) => {
      const options = new Set<string>();

      data.forEach((row) => {
        const value = getComparableValue(row[key]);

        if (
          (typeof value === 'string' && value.trim() !== '') ||
          typeof value === 'number' ||
          typeof value === 'boolean'
        ) {
          options.add(String(value));
        }
      });

      return Array.from(options).sort((a, b) => a.localeCompare(b));
    },
    [data, getComparableValue],
  );

  const handleFilterChange = useCallback(
    (key: keyof T & string, value: string) => {
      setFilters((prevFilters) => {
        if (value === '') {
          if (!(key in prevFilters)) {
            return prevFilters;
          }

          const { [key]: _, ...rest } = prevFilters;
          return rest;
        }

        return { ...prevFilters, [key]: value };
      });
    },
    [],
  );

  const clearFilters = useCallback(() => {
    if (Object.keys(defaultFilters).length === 0) {
      setFilters({});
      return;
    }
    setFilters({ ...defaultFilters });
  }, [defaultFilters]);

  /**
   * Encontra o cabeçalho que não possui chave definida (ícone)
   */
  const iconHeader = useMemo(
    () => headers.find((header) => !String(header.key ?? '').trim()),
    [headers],
  );

  /**
   * Filtra os cabeçalhos para remover aqueles sem chave definida
   */
  const dataHeaders = useMemo(
    () =>
      headers.filter((header): header is CustomTableDataHeader<T> =>
        Boolean(String(header.key ?? '').trim()),
      ),
    [headers],
  );

  const headerConfigMap = useMemo(() => {
    const map = new Map<string, CustomTableDataHeader<T>>();
    dataHeaders.forEach((header) => {
      map.set(header.key, header);
    });
    return map;
  }, [dataHeaders]);

  const filteredData = useMemo(() => {
    if (!filterable) return data;

    const hasActiveFilters = Object.values(filters).some(
      (filterValue) => filterValue && filterValue.trim() !== '',
    );

    if (!hasActiveFilters) return data;

    return data.filter((row) =>
      Object.entries(filters).every(([key, filterValue]) => {
        if (!filterValue) return true;

        const headerKey = key as keyof T & string;
        const headerConfig = headerConfigMap.get(headerKey);
        const fieldType = headerConfig?.fieldType ?? 'text';
        const comparableValue = getComparableValue(row[headerKey]);

        if (comparableValue === null) {
          return false;
        }

        if (fieldType === 'text') {
          return String(comparableValue)
            .toLowerCase()
            .includes(filterValue.toLowerCase());
        }

        if (fieldType === 'select') {
          return String(comparableValue) === filterValue;
        }

        if (fieldType === 'number') {
          return Number(comparableValue) === Number(filterValue);
        }

        if (fieldType === 'date') {
          return normalizeDateValue(String(comparableValue)) === filterValue;
        }

        if (fieldType === 'checkbox') {
          if (filterValue === '') return true;

          const expectedValue = filterValue === 'true';

          return comparableValue === expectedValue;
        }

        if (fieldType === 'booleanSelect') {
          if (filterValue === '') return true;
          const expectedValue = filterValue === 'true';
          return comparableValue === expectedValue;
        }

        return true;
      }),
    );
  }, [
    data,
    filterable,
    filters,
    getComparableValue,
    headerConfigMap,
    normalizeDateValue,
  ]);

  useEffect(() => {
    setCurrentPage(1);
  }, [rowsPerPage, filteredData]);

  const sortedData = useMemo(() => {
    if (!sortConfig) return filteredData;

    const sorted = [...filteredData].sort((a, b) =>
      compareValues(a[sortConfig.key], b[sortConfig.key], sortConfig.direction),
    );

    return sorted;
  }, [filteredData, sortConfig]);

  const totalItems = sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const currentPageSafe = Math.min(currentPage, totalPages);
  const startIndex = (currentPageSafe - 1) * rowsPerPage;
  const paginatedData = sortedData.slice(startIndex, startIndex + rowsPerPage);

  const handleSort = (key: keyof T & string) => {
    if (!sortable) return;

    setSortConfig((prev) => {
      if (prev?.key === key) {
        const nextDirection: SortDirection =
          prev.direction === 'asc' ? 'desc' : 'asc';
        return { key, direction: nextDirection };
      }
      return { key, direction: 'asc' };
    });
  };

  const handleItemsPerPageChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setRowsPerPage(Number(event.target.value));
  };

  const handleGoToPage = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const page = Number(goToPageValue);
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
    setGoToPageValue('');
  };

  const showingStart = totalItems === 0 ? 0 : startIndex + 1;
  const showingEnd = Math.min(startIndex + rowsPerPage, totalItems);

  return (
    <TableSection minHeight={minHeight}>
      <TableScroll>
        {data.length === 0 ? (
          <EmptyMessage>{emptyMessage}</EmptyMessage>
        ) : (
          <StyledTable role="table" data-testid={testId}>
            <thead>
              <HeaderRow>
                {showIconOffcanvas && (
                  <HeaderCell
                    $sortable={false}
                    style={
                      iconHeader?.width
                        ? { width: `${iconHeader.width}%` }
                        : undefined
                    }
                  />
                )}
                {dataHeaders.map((header) => {
                  const isSorted = sortConfig?.key === header.key;
                  const widthStyle = header.width
                    ? { width: `${header.width}%` }
                    : undefined;
                  return (
                    <HeaderCell
                      key={header.key}
                      $sortable={sortable}
                      onClick={
                        sortable ? () => handleSort(header.key) : undefined
                      }
                      aria-sort={
                        isSorted
                          ? sortConfig?.direction === 'asc'
                            ? 'ascending'
                            : 'descending'
                          : 'none'
                      }
                      style={{
                        ...widthStyle,
                        textAlign: header.textAlign ?? 'left',
                      }}
                      data-testid={
                        testId
                          ? `${testId}-header-${String(header.key)}`
                          : undefined
                      }
                    >
                      <HeaderContent>
                        {header.text}
                        {sortable && (
                          <span>
                            {isSorted ? (
                              sortConfig?.direction === 'asc' ? (
                                <FaAngleUp />
                              ) : (
                                <FaAngleDown />
                              )
                            ) : (
                              <FaAngleDown style={{ opacity: 0.3 }} />
                            )}
                          </span>
                        )}
                      </HeaderContent>
                    </HeaderCell>
                  );
                })}
              </HeaderRow>
              {filterable && (
                <FilterTable
                  headers={dataHeaders}
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onClearFilters={clearFilters}
                  getUniqueOptions={getUniqueOptions}
                  showIconOffcanvas={showIconOffcanvas}
                  placeholder={filterPlaceholder}
                  data-testid={testId ? `${testId}-filters` : undefined}
                />
              )}
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <BodyRow $clickable={false}>
                  <BodyCell
                    colSpan={dataHeaders.length + (showIconOffcanvas ? 1 : 0)}
                    style={{ textAlign: 'center', fontStyle: 'italic' }}
                    data-testid={testId ? `${testId}-empty-message` : undefined}
                  >
                    {emptyMessage}
                  </BodyCell>
                </BodyRow>
              ) : (
                paginatedData.map((row, index) => (
                  <Row
                    key={`row-${index}`}
                    row={row}
                    index={startIndex + index}
                    headers={dataHeaders}
                    onRowClick={onRowClick}
                    showIconOffcanvas={showIconOffcanvas}
                    onClickOffCanvas={onClickOffCanvas}
                    testId={testId}
                  />
                ))
              )}
            </tbody>
          </StyledTable>
        )}
      </TableScroll>

      {showPagination && totalItems > 0 && (
        <Footer>
          <Pagination
            rowsPerPage={rowsPerPage}
            totalItems={totalItems}
            paginate={setCurrentPage}
            currentPage={currentPageSafe}
            showingStart={showingStart}
            showingEnd={showingEnd}
            handleItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={DEFAULT_PAGE_SIZES}
            goToPageValue={goToPageValue}
            onGoToPageChange={setGoToPageValue}
            onGoToPageSubmit={handleGoToPage}
            data-testid={testId ? `${testId}-pagination` : undefined}
          />
        </Footer>
      )}
    </TableSection>
  );
};

interface RowProps<T extends Record<string, unknown>> {
  row: T;
  index: number;
  headers: Array<CustomTableDataHeader<T>>;
  onRowClick?: (row: T, index: number) => void;
  showIconOffcanvas: boolean;
  onClickOffCanvas?: (row: T, index: number) => void;
  testId?: string;
}

const Row = <T extends Record<string, unknown>>({
  row,
  index,
  headers,
  onRowClick,
  showIconOffcanvas,
  onClickOffCanvas,
  testId,
}: RowProps<T>) => {
  const handleRowClick = () => {
    if (onRowClick) {
      onRowClick(row, index);
    }
  };

  return (
    <BodyRow
      $clickable={Boolean(onRowClick)}
      onClick={onRowClick ? handleRowClick : undefined}
      data-testid={testId ? `${testId}-row-${index}` : undefined}
    >
      {showIconOffcanvas && (
        <IconCell>
          <RowActionButton
            className="material-icons"
            role="button"
            aria-label="Abrir detalhes"
            onClick={(event) => {
              event.stopPropagation();
              onClickOffCanvas?.(row, index);
            }}
            data-testid={testId ? `${testId}-open-details-${index}` : undefined}
          >
            zoom_in
          </RowActionButton>
        </IconCell>
      )}
      {headers.map((header) => (
        <BodyCell
          key={`${header.key}-${index}`}
          style={{ textAlign: header.textAlign ?? 'left' }}
          data-testid={
            testId ? `${testId}-cell-${String(header.key)}-${index}` : undefined
          }
        >
          {header.renderCell
            ? header.renderCell(row[header.key], row)
            : asNode(row[header.key])}
        </BodyCell>
      ))}
    </BodyRow>
  );
};

export default CustomTable;
